import type { InsightsSavedView, SavedViewsListResponse } from '#services/api/saved-views.ts';
import type { loader } from './Home.data.ts';
import type { HomeTab } from './Home.utils.ts';

import * as React from 'react';
import { faMessageBot } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeScalableIcon, styled } from '@mend/mui';
import { Box, Button, Chip, Stack, Tab, Tabs } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLoaderData, useSearchParams } from 'react-router';

import { savedViews } from '#lib/react-query/queries.ts';
import { ActiveProviders } from '#routes/Home/Widgets/ActiveProviders.tsx';
import { FormCompletionJourney } from '#routes/Home/Widgets/FormCompletionJourney.tsx';
import { FormCompletionTimeTrends } from '#routes/Home/Widgets/FormCompletionTimeTrends.tsx';
import * as DataAgent from './DataAgent.tsx';
import { Address } from './Filters/Address.tsx';
import { AppointmentTypes } from './Filters/AppointmentTypes.tsx';
import { AssessmentType } from './Filters/AssessmentType.tsx';
import { BookingChannel } from './Filters/BookingChannel.tsx';
import { DateRange } from './Filters/DateRange.tsx';
import { FiltersProvider } from './Filters/FiltersContext.tsx';
import { FormPriority } from './Filters/FormPriority.tsx';
import { Provider } from './Filters/Provider.tsx';
import { serializeFilters } from './Home.utils.ts';
import { Readiness } from './Readiness/Readiness.tsx';
import { SavedViewButtonGroup } from './SavedViews/SavedViewButtonGroup.tsx';
import { SavedViewDialog } from './SavedViews/SavedViewDialog.tsx';
import { AggregatedAppointmentsByType } from './Widgets/AggregatedAppointmentsByType.tsx';
import { AppointmentRates } from './Widgets/AppointmentRates.tsx';
import { AppointmentsByType } from './Widgets/AppointmentsByType.tsx';
import { FormCompletionByType } from './Widgets/FormCompletionByType.tsx';
import { FormTrends } from './Widgets/FormTrends/FormTrends.tsx';
import { FormTypeBreakdown } from './Widgets/FormTypeBreakdown.tsx';
import { ActiveProvidersRate } from './Widgets/Stats/ActiveProvidersRate.tsx';
import { FormCompletionTime } from './Widgets/Stats/FormCompletionTime.tsx';
import { FormsAssigned } from './Widgets/Stats/FormsAssigned.tsx';
import { FormsCompleted } from './Widgets/Stats/FormsCompleted.tsx';
import { FormsCompletionRate } from './Widgets/Stats/FormsCompletionRate.tsx';
import { NoShowRate } from './Widgets/Stats/NoShowRate.tsx';
import { TotalAppointments } from './Widgets/Stats/TotalAppointments.tsx';
import { TotalPatientsSeen } from './Widgets/Stats/TotalPatientsSeen.tsx';

const Grid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

const FiltersGrid = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

const StatWidgetGrid = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

function select(savedViews: SavedViewsListResponse) {
  const newShape: { scheduling: InsightsSavedView[]; forms: InsightsSavedView[] } = {
    scheduling: [],
    forms: [],
  };

  savedViews.views.forEach((view) => {
    newShape[view.tabType].push(view);
  });

  return newShape;
}

export function Home() {
  const data = useLoaderData<ReturnType<typeof loader>>();
  const [searchParams, setSearchParams] = useSearchParams();
  const savedViewsQuery = useQuery({
    ...savedViews,
    initialData: data.savedViewsData,
    select,
  });

  const [saveModalOpen, setSaveModalOpen] = React.useState(false);
  const [isAskMiaDrawerOpen, setAskMiaDrawerOpen] = React.useState(false);

  const currentTab = data.tab;
  const viewParam = Number(searchParams.get('view'));

  function handleTabChange(_event: React.SyntheticEvent, value: string) {
    if (value === 'coordinate') {
      setSearchParams({ tab: value });
      return;
    }

    setSearchParams((prev) => {
      prev.set('tab', value);
      prev.delete('view');
      return prev;
    });
  }

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Section tabs"
          sx={{ width: '100%' }}
        >
          <Tab label="Scheduling" value="scheduling" {...getRouteTabProps('scheduling')} />
          <Tab label="Forms" value="forms" {...getRouteTabProps('forms')} />
          <Tab
            label={
              <Stack component="span" direction="row" alignItems="center" spacing={0.75}>
                <Box component="span">Coordinate</Box>
                <Chip component="span" label="Beta" size="small" aria-hidden />
              </Stack>
            }
            aria-label="Coordinate"
            value="coordinate"
            {...getRouteTabProps('coordinate')}
          />
        </Tabs>

        <Button
          variant="outlined"
          color="neutral"
          sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' } }}
          onClick={() => setAskMiaDrawerOpen(true)}
          startIcon={
            <FontAwesomeScalableIcon icon={faMessageBot} fontSize="inherit" color="inherit" />
          }
        >
          Ask about my practice
        </Button>
      </Stack>

      {currentTab !== 'coordinate' ? (
        <FiltersProvider key={currentTab} activeView={data.activeView} tabType={currentTab}>
          <Stack mb={4} spacing={0} gap={2} alignItems="flex-start">
            {savedViewsQuery.data[currentTab].length > 0 && (
              <Stack spacing={0} gap={1} flexWrap="wrap">
                {savedViewsQuery.data[currentTab].map((view) => (
                  <SavedViewButtonGroup
                    key={view.id}
                    view={view}
                    isActive={viewParam === view.id}
                    getSerializedFilters={() =>
                      serializeFilters({
                        parsed: data,
                        tabType: view.tabType,
                      })
                    }
                  />
                ))}
              </Stack>
            )}

            <Button
              variant="text"
              color="neutral"
              startIcon={<FontAwesomeScalableIcon icon={faPlus} />}
              onClick={() => setSaveModalOpen(true)}
              sx={{ whiteSpace: 'nowrap', ml: 'auto' }}
            >
              Save as View
            </Button>
          </Stack>

          <FiltersGrid key={data.activeView?.id ?? 0}>
            <DateRange defaultValue={[data.from, data.to]} />
            <Provider defaultValue={data.providerIds} />
            {currentTab === 'scheduling' ? (
              <>
                <Address defaultValue={data.addressIds} />
                <BookingChannel defaultValue={data.bookingChannelIds} />
                <AppointmentTypes
                  defaultValue={{
                    baseAppointmentTypes: data.baseAppointmentTypes,
                    appointmentTypeIds: data.appointmentTypeIds,
                  }}
                />
              </>
            ) : (
              <>
                <AssessmentType defaultValue={data.assessmentTypeIds} />
                <FormPriority defaultValue={data.formsPriority} />
              </>
            )}
          </FiltersGrid>

          {/*
          Render the route dialogs that can be opened from the widgets. This placement works here
          as of BAD-37 because only portal'ed elements are rendered as nested routes. Once this
          stops being the case, the approach will have to be reevaluated.
        */}
          <Outlet />

          {saveModalOpen && (
            <SavedViewDialog
              onClose={() => setSaveModalOpen(false)}
              onSave={(viewId) => {
                setSaveModalOpen(false);
                setSearchParams({ view: String(viewId) });
              }}
              tabType={currentTab}
              getFilters={() => serializeFilters({ parsed: data, tabType: currentTab })}
            />
          )}
        </FiltersProvider>
      ) : null}

      <TabPanel tab="scheduling" activeTab={currentTab}>
        <StatWidgetGrid>
          <ActiveProvidersRate />
          <TotalPatientsSeen />
          <TotalAppointments />
          <NoShowRate />
        </StatWidgetGrid>

        <Grid>
          <AppointmentRates />
          <AppointmentsByType />
          <AggregatedAppointmentsByType />
          <ActiveProviders />
        </Grid>
      </TabPanel>

      <TabPanel tab="forms" activeTab={currentTab}>
        <StatWidgetGrid>
          <FormsAssigned />
          <FormsCompleted />
          <FormsCompletionRate />
          <FormCompletionTime />
        </StatWidgetGrid>

        <Grid>
          <FormTrends />
          <FormCompletionJourney />
          <FormTypeBreakdown />
          <FormCompletionByType />
          <FormCompletionTimeTrends />
        </Grid>
      </TabPanel>

      <TabPanel tab="coordinate" activeTab={currentTab}>
        <Readiness me={data.me} />
      </TabPanel>

      <DataAgent.Drawer open={isAskMiaDrawerOpen} onClose={() => setAskMiaDrawerOpen(false)}>
        <DataAgent.Iframe />
      </DataAgent.Drawer>
    </>
  );
}

function getRouteTabProps(tab: string) {
  return {
    id: `sections-tab-${tab}`,
    'aria-controls': `sections-tabpanel-${tab}`,
  };
}

interface TabPanelProps {
  tab: HomeTab;
  activeTab: HomeTab;
  children: React.ReactNode;
}

/**
 * TODO: Once the activity component is stable, we should test it here so that we can render the
 * children of all tab panels and potentially improve performance.
 *
 * @see https://react.dev/reference/react/Activity
 */
function TabPanel(props: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={props.activeTab !== props.tab}
      id={`sections-tabpanel-${props.tab}`}
      aria-labelledby={`sections-tab-${props.tab}`}
    >
      {props.activeTab === props.tab ? props.children : null}
    </Box>
  );
}
